import { useEffect, useState } from 'react'
import { Alert, Button, Card, Input, Space, Typography, Upload } from 'antd'
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import {
  fetchContractTemplate,
  removeLetterhead,
  saveContractTerms,
  uploadLetterhead,
  type ContractTemplate,
} from './contractApi'

const TERMS_MAX = 8000

/** Shartnoma blankasi (A4 fon rasmi) va shartlar matni (ADR-051). */
export function ContractTemplateCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const template = useQuery({ queryKey: queryKeys.contracts.template, queryFn: ({ signal }) => fetchContractTemplate(signal) })
  const [terms, setTerms] = useState('')

  useEffect(() => {
    if (template.data) setTerms(template.data.terms ?? '')
  }, [template.data])

  const saved = (next: ContractTemplate) => queryClient.setQueryData(queryKeys.contracts.template, next)
  const saveTerms = useMutation({ mutationFn: () => saveContractTerms(terms.trim() || null), onSuccess: saved })
  const upload = useMutation({ mutationFn: uploadLetterhead, onSuccess: saved })
  const remove = useMutation({ mutationFn: removeLetterhead, onSuccess: saved })
  const failure = saveTerms.error ?? upload.error ?? remove.error ?? template.error
  const letterheadUrl = template.data?.letterheadUrl ?? null

  return (
    <Card title={t('settings.contract.title')} loading={template.isPending}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{t('settings.contract.intro')}</Typography.Text>
        {failure ? <Alert type="error" showIcon message={translateError(failure, t)} /> : null}
        <div>
          <Typography.Text strong>{t('settings.contract.letterhead')}</Typography.Text>
          <div style={{ margin: '8px 0' }}>
            {letterheadUrl ? (
              <img src={letterheadUrl} alt="" style={{ width: 140, border: '1px solid #ddd' }} />
            ) : (
              <Typography.Text type="secondary">{t('settings.contract.noLetterhead')}</Typography.Text>
            )}
          </div>
          <Space wrap>
            <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              beforeUpload={(file) => {
                upload.mutate(file)
                return false
              }}
            >
              <Button icon={<UploadOutlined />} loading={upload.isPending}>
                {letterheadUrl ? t('settings.contract.replaceLetterhead') : t('settings.contract.uploadLetterhead')}
              </Button>
            </Upload>
            {letterheadUrl ? (
              <Button danger icon={<DeleteOutlined />} loading={remove.isPending} onClick={() => remove.mutate()}>
                {t('settings.contract.removeLetterhead')}
              </Button>
            ) : null}
          </Space>
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            {t('settings.contract.letterheadHint')}
          </Typography.Paragraph>
        </div>
        <div>
          <Typography.Text strong>{t('settings.contract.terms')}</Typography.Text>
          <Input.TextArea
            dir="auto"
            rows={10}
            maxLength={TERMS_MAX}
            showCount
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
            style={{ marginTop: 8 }}
          />
        </div>
        <Button
          type="primary"
          loading={saveTerms.isPending}
          disabled={terms.trim() === (template.data?.terms ?? '')}
          onClick={() => saveTerms.mutate()}
        >
          {t('common.save')}
        </Button>
      </Space>
    </Card>
  )
}
